import { Injectable, inject } from '@angular/core';
import { NavController } from '@ionic/angular/standalone';
import { Observable, firstValueFrom, race, timer } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { Business } from '../../models/Business';
import { Charity } from '../../models/Charity';
import { BusinessService } from '../business/business.service';
import { CharityService } from '../charity/charity.service';
import { LoggerService } from '../logger/logger.service';
import { StateType } from '../../models/StateType';
import { TabService } from './tab.service';

const SWITCH_TIMEOUT_MS: number = 10000;

type Portal = 'charity' | 'business';
type BeforeNavigate = () => void | Promise<void>;

@Injectable({
  providedIn: 'root',
})
export class PortalSwitchService {
  private readonly charityService = inject(CharityService);
  private readonly businessService = inject(BusinessService);
  private readonly navController = inject(NavController);
  private readonly tabService = inject(TabService);
  private readonly logger = inject(LoggerService);

  async switchToCharity(
    charityId: string | number | null | undefined,
    beforeNavigate?: BeforeNavigate,
  ): Promise<boolean> {
    const targetCharityId: string | null = this.normalizeId(charityId);
    if (!targetCharityId) {
      return false;
    }

    this.charityService.setCurrentCharity(targetCharityId);
    return this.waitAndNavigate(
      'charity',
      this.charityService.charity$,
      targetCharityId,
      '/charity/charity-info',
      beforeNavigate,
    );
  }

  async switchToBusiness(
    businessId: string | number | null | undefined,
    beforeNavigate?: BeforeNavigate,
  ): Promise<boolean> {
    const targetBusinessId: string | null = this.normalizeId(businessId);
    if (!targetBusinessId) {
      return false;
    }

    this.businessService.setCurrentBusiness(targetBusinessId);
    return this.waitAndNavigate(
      'business',
      this.businessService.business$,
      targetBusinessId,
      '/business/business-info',
      beforeNavigate,
    );
  }

  private async waitAndNavigate<T extends Charity | Business>(
    portal: Portal,
    org$: Observable<T | null>,
    targetOrgId: string,
    fallbackUrl: string,
    beforeNavigate?: BeforeNavigate,
  ): Promise<boolean> {
    let org: T | null;
    try {
      org = await firstValueFrom(
        race(
          org$.pipe(
            filter((loadedOrg): loadedOrg is T => this.orgMatches(loadedOrg, targetOrgId)),
            take(1),
          ),
          timer(SWITCH_TIMEOUT_MS).pipe(map((): null => null)),
        ).pipe(take(1)),
      );
    } catch {
      return false;
    }

    if (!org) {
      return false;
    }

    try {
      await beforeNavigate?.();
    } catch (error) {
      // Menu-close (or other pre-nav cleanup) failing must not strand the user mid-switch —
      // log and fall through to navigate anyway.
      this.logger.error('Portal switch beforeNavigate callback failed', error, { portal, targetOrgId });
    }

    const lastPage: { url: string; state: StateType } | null = this.tabService.getLastPage(portal);
    try {
      await this.navController.navigateRoot(lastPage?.url || fallbackUrl, {
        state: lastPage?.state || {},
      });
    } catch (error) {
      this.logger.error('Portal switch navigation failed', error, { portal, targetOrgId });
      return false;
    }
    return true;
  }

  private orgMatches(org: Charity | Business | null, targetOrgId: string): boolean {
    return org !== null && this.normalizeId(org.id) === targetOrgId;
  }

  private normalizeId(id: string | number | null | undefined): string | null {
    if (id === null || id === undefined) {
      return null;
    }

    const normalized: string = String(id).trim();
    return normalized.length > 0 ? normalized : null;
  }
}
